
import {
    collection,
    getDocs,
    addDoc,
    deleteDoc,
    doc,
    updateDoc,
    query,
    where,
    setDoc,
    getDoc,
    getDocFromServer,
    increment,
    onSnapshot
} from 'firebase/firestore';
import { db } from '../lib/firebase';
import { WardrobeItem } from '../types';

export const PRODUCTS_COLLECTION = 'products';

export async function getProducts(): Promise<WardrobeItem[]> {
    const querySnapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
    return querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
    } as WardrobeItem));
}

export async function addProduct(product: Omit<WardrobeItem, 'id'>) {
    return await addDoc(collection(db, PRODUCTS_COLLECTION), product);
}

// Function to seed initial data
export async function seedProducts(products: WardrobeItem[]) {
    // Check if empty
    const snapshot = await getDocs(collection(db, PRODUCTS_COLLECTION));
    if (!snapshot.empty) return; // Already seeded

    const promises = products.map(product => {
        // Use custom ID if provided in the object, otherwise auto-id
        const { id, ...data } = product;
        return setDoc(doc(db, PRODUCTS_COLLECTION, id), data);
    });

    await Promise.all(promises);
}
// --- User Favorites ---
export const USERS_COLLECTION = 'users';

export async function getUserFavorites(userId: string): Promise<string[]> {
    const userDoc = await getDocs(query(collection(db, USERS_COLLECTION, userId, 'favorites')));
    return userDoc.docs.map(doc => doc.id);
}

export async function toggleFavorite(userId: string, productId: string, isFavorite: boolean) {
    const favoriteDocRef = doc(db, USERS_COLLECTION, userId, 'favorites', productId);
    if (isFavorite) {
        await setDoc(favoriteDocRef, { addedAt: new Date().toISOString() });
    } else {
        await deleteDoc(favoriteDocRef);
    }
}

// --- Cart Management ---
export interface SavedCartItem {
    id: string; // productId
    quantity: number;
    selectedSize: string;
    selectedColor: string;
}

export async function syncCart(userId: string, items: SavedCartItem[]) {
    // For simplicity, we'll overwrite the cart collection for this user
    // In a production app, you might want to merge or handle conflicts
    const cartCollection = collection(db, USERS_COLLECTION, userId, 'cart');
    const existingItems = await getDocs(cartCollection);

    // Clear existing
    const deletePromises = existingItems.docs.map(d => deleteDoc(doc(db, USERS_COLLECTION, userId, 'cart', d.id)));
    await Promise.all(deletePromises);

    // Add new
    const addPromises = items.map(item =>
        setDoc(doc(db, USERS_COLLECTION, userId, 'cart', item.id), item)
    );
    await Promise.all(addPromises);
}

export async function getUserCart(userId: string): Promise<SavedCartItem[]> {
    const cartSnapshot = await getDocs(collection(db, USERS_COLLECTION, userId, 'cart'));
    return cartSnapshot.docs.map(doc => doc.data() as SavedCartItem);
}

// --- Subscription & Usage Tracking ---
export interface UserStats {
    tryOnCount: number;
    allowedTryOnCount: number;
    isSubscribed: boolean;
}

export async function getUserStats(userId: string): Promise<UserStats> {
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');

    try {
        // Try to get from server first to ensure fresh data
        const statsDoc = await getDocFromServer(statsRef);

        if (!statsDoc.exists()) {
            return { tryOnCount: 0, allowedTryOnCount: 3, isSubscribed: false };
        }
        console.log("getUserStats: Fetched from server:", statsDoc.data());
        return statsDoc.data() as UserStats;
    } catch (error: any) {
        // If offline, fall back to cache
        console.warn("getUserStats: Server fetch failed, trying cache...", error.message);
        try {
            const statsDoc = await getDoc(statsRef);
            if (!statsDoc.exists()) {
                return { tryOnCount: 0, allowedTryOnCount: 3, isSubscribed: false };
            }
            console.log("getUserStats: Fetched from cache:", statsDoc.data());
            return statsDoc.data() as UserStats;
        } catch (cacheError) {
            console.error("getUserStats: Cache also failed:", cacheError);
            return { tryOnCount: 0, allowedTryOnCount: 3, isSubscribed: false };
        }
    }
}

export function subscribeToUserStats(userId: string, onUpdate: (stats: UserStats) => void) {
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');

    return onSnapshot(statsRef, (docSnapshot) => {
        if (docSnapshot.exists()) {
            onUpdate(docSnapshot.data() as UserStats);
        } else {
            // Return default if not exists yet
            onUpdate({ tryOnCount: 0, allowedTryOnCount: 3, isSubscribed: false });
        }
    }, (error) => {
        console.error("Error subscribing to stats:", error);
    });
}

export async function incrementTryOnCount(userId: string) {
    console.log(`db: Incrementing try-on count for user ${userId}...`);
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');

    try {
        const snap = await getDoc(statsRef);

        if (!snap.exists()) {
            // Create for the first time
            await setDoc(statsRef, {
                tryOnCount: 1,
                allowedTryOnCount: 3,
                isSubscribed: false
            });
        } else {
            // Update existing
            await updateDoc(statsRef, {
                tryOnCount: increment(1)
            });
        }
        console.log("db: Try-on count incremented successfully.");
    } catch (error) {
        console.error("db: Error incrementing try-on count:", error);
        throw error;
    }
}

export async function addUserCredits(userId: string, additionalCredits: number) {
    console.log(`db: Adding ${additionalCredits} credits for user ${userId}...`);
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');

    try {
        const snap = await getDoc(statsRef);
        if (!snap.exists()) {
            await setDoc(statsRef, {
                tryOnCount: 0,
                allowedTryOnCount: 3 + additionalCredits,
                isSubscribed: true
            });
        } else {
            await updateDoc(statsRef, {
                allowedTryOnCount: increment(additionalCredits),
                isSubscribed: true
            });
        }
        console.log("db: Credits added successfully.");
    } catch (error) {
        console.error("db: Error adding credits:", error);
        throw error;
    }
}

export async function updateUserSubscription(userId: string, isSubscribed: boolean) {
    console.log(`db: Updating subscription for user ${userId} to ${isSubscribed}...`);
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');

    try {
        await setDoc(statsRef, { isSubscribed }, { merge: true });
        console.log("db: Subscription updated successfully.");
    } catch (error) {
        console.error("db: Error updating subscription:", error);
        throw error;
    }
}
// --- History Management ---
export interface TryOnResult {
    id: string; // Document ID
    imageUrl: string;
    garment: WardrobeItem | null;
    timestamp: any; // Firestore Timestamp
}

export async function saveTryOnResult(userId: string, imageUrl: string, garment: WardrobeItem | null) {
    console.log(`db: Saving try-on result for user ${userId}...`);
    try {
        await addDoc(collection(db, USERS_COLLECTION, userId, 'history'), {
            imageUrl,
            garment,
            timestamp: new Date()
        });
        console.log("db: Try-on result saved.");
    } catch (error) {
        console.error("db: Error saving try-on result:", error);
        // Don't throw, just log. History is non-critical.
    }
}

export async function getTryOnHistory(userId: string): Promise<TryOnResult[]> {
    try {
        const q = query(collection(db, USERS_COLLECTION, userId, 'history'), where('timestamp', '!=', null));
        // Note: Composite index might be needed for ordering by timestamp desc.
        // For now, let's just get them and sort in JS to avoid index errors during demo.
        const snapshot = await getDocs(q);
        const results = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as TryOnResult));

        // Sort descending
        return results.sort((a, b) => b.timestamp.seconds - a.timestamp.seconds);
    } catch (error) {
        console.error("db: Error fetching history:", error);
        return [];
    }
}

export function subscribeToTryOnHistory(userId: string, onUpdate: (history: TryOnResult[]) => void) {
    const q = query(
        collection(db, USERS_COLLECTION, userId, 'history'),
        where('timestamp', '!=', null)
    );

    return onSnapshot(q, (snapshot) => {
        const results = snapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
        } as TryOnResult));

        // Sort locally to ensure correct order without complex index
        results.sort((a, b) => {
            const tA = a.timestamp?.seconds || 0;
            const tB = b.timestamp?.seconds || 0;
            return tB - tA;
        });

        onUpdate(results);
    }, (error) => {
        console.error("db: Error subscribing to history:", error);
    });
}

// --- User Profile & Admin ---
export interface UserProfileData {
    id: string;
    email: string;
    displayName: string;
    createdAt?: string;
    lastLoginAt?: string;
    stats?: UserStats; // detailed stats
}

export async function saveUserProfile(user: { uid: string; email: string | null; displayName: string | null }) {
    if (!user.email) return;

    const userRef = doc(db, USERS_COLLECTION, user.uid);
    try {
        await setDoc(userRef, {
            email: user.email,
            displayName: user.displayName || 'User',
            lastLoginAt: new Date().toISOString()
        }, { merge: true });
    } catch (error) {
        console.error("db: Error saving user profile:", error);
    }
}

export async function getAllUsers(): Promise<UserProfileData[]> {
    try {
        const usersSnap = await getDocs(collection(db, USERS_COLLECTION));
        const users: UserProfileData[] = [];

        await Promise.all(usersSnap.docs.map(async (userDoc) => {
            const userData = userDoc.data();
            // Skip if it doesn't look like a user doc (e.g. if it only has subcollections)
            if (!userData.email) return;

            // Fetch stats for this user
            const stats = await getUserStats(userDoc.id);

            users.push({
                id: userDoc.id,
                email: userData.email,
                displayName: userData.displayName || 'User',
                createdAt: userData.createdAt,
                lastLoginAt: userData.lastLoginAt,
                stats: stats
            });
        }));

        return users;
    } catch (error) {
        console.error("db: Error fetching all users:", error);
        return [];
    }
}

export async function updateUserBalance(userId: string, newBalance: number) {
    const statsRef = doc(db, USERS_COLLECTION, userId, 'stats', 'usage');
    try {
        await setDoc(statsRef, {
            allowedTryOnCount: newBalance
        }, { merge: true });
        console.log(`db: Updated balance for user ${userId} to ${newBalance}`);
    } catch (error) {
        console.error("db: Error updating user balance:", error);
        throw error;
    }
}
